import React from 'react';
import StatusPill from './StatusPill';

interface JobsiteCardProps {
    site: {
        id: string;
        jobSiteName: string;
        status?: string;
        sector?: { name: string };
        jobTrades?: { tradesType?: { name: string } };
        addresses?: {
            address1: string;
            address2?: string;
            city: string;
            coordinates?: {
                coordinates: number[];
            };
        };
        userId?: { name: string };
        jobCompletionStatus?: { status: string };
        workers?: {
            labels: string[];
            noOfWorkers: string;
            worksType?: string;
        };
    };
    onClick: () => void;
    onNameClick: () => void;
    getCompletionPercentage: (status: string) => number;
    showMapButton?: boolean;
}

const JobsiteCard: React.FC<JobsiteCardProps> = ({ site, onClick, onNameClick, getCompletionPercentage, showMapButton = true }) => {
    const hasCoordinates = !!(site.addresses?.coordinates?.coordinates?.length);
    return (
        <div key={site.id} className="bg-white rounded-md border border-[#CED4DA] p-3 sm:p-4">
                <h3 
                    className="text-[20px] font-[700] text-[#A52A2A] cursor-pointer hover:underline transition-colors duration-200" 
                    onClick={(e) => {
                        e.stopPropagation();
                        onNameClick();
                    }}
                    title={site.jobSiteName.length > 24 ? site.jobSiteName : ''}
                >
                    {site.jobSiteName.length > 24 ? `${site.jobSiteName.substring(0, 24)}...` : site.jobSiteName}
                </h3>
            <div className="text-[14px] font-[400] text-[#000000] mb-2">
                {(() => {
                    if (!site.addresses) return 'No address available';
                    
                    const address1 = site.addresses.address1;
                    const address2 = site.addresses.address2;
                    const city = site.addresses.city;
                    
                    const fullAddress = [
                        address1,
                        address2,
                        city
                    ].filter(Boolean).join(', ');
                    
                    return fullAddress;
                })()}
            </div>
            <div className="flex items-center gap-3 text-[#000000] mb-2 text-[14px] font-[400]">
                <span>Submitted by <strong>{site.userId?.name || 'Unknown'}</strong></span>
            </div>

            <div className='flex justify-between'>
                {/* Status indicators */}
                <div className="items-center mb-2">
                    {/* Operational Status */}
                    <div className="mb-2 flex items-center gap-2">
                        <span className="text-[#000000] text-[14px] font-[400]">Status:</span>
                        <StatusPill status={site.status} size="sm" />
                    </div>

                    {/* Completion Status */}
                    <div>
                        <span className="text-[#000000] mb-2 text-[14px] font-[400]">Completion Status</span>
                        <span className="ml-2 text-[#000000] mb-2 text-[14px] font-[400]">
                            <strong>{site.jobCompletionStatus?.status || 'N/A'} - {getCompletionPercentage(site.jobCompletionStatus?.status || 'Start')}%</strong>
                        </span>
                    </div>
                    <div className="w-64 bg-gray-200 rounded-full h-2.5">
                        <div
                            className="bg-[#A42823] h-2.5 rounded-full"
                            style={{ width: `${getCompletionPercentage(site.jobCompletionStatus?.status || 'Start')}%` }}
                        ></div>
                    </div>
                </div>

                <div className="flex flex-col gap-2 items-center justify-center">
                    {showMapButton && (
                        <button
                            className={`rounded-full flex items-center justify-center ${hasCoordinates ? 'btn-primary cursor-pointer' : 'bg-gray-300 cursor-not-allowed'}`}
                            style={{ width: '36px', height: '36px' }}
                            onClick={(e) => { e.stopPropagation(); if (hasCoordinates) onNameClick(); }}
                            disabled={!hasCoordinates}
                            title={hasCoordinates ? 'Highlight on map' : 'No coordinates available'}
                        >
                            <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                            </svg>
                        </button>
                    )}
                    <button
                        className="btn-primary rounded-full cursor-pointer flex items-center justify-center"
                        style={{ width: '36px', height: '36px' }}
                        onClick={(e) => { e.stopPropagation(); onClick(); }}
                        title="View details"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="white" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5-5 5M6 12h12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobsiteCard; 